import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Spinner, Alert } from 'reactstrap';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createMeterReading } from '../store/meter/actions';

const MeterReadingForm = ({ isOpen, toggle, meterId, meterName, onSave }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { saving, saveError } = useSelector((state) => state.Meter);

  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');
  const [valueError, setValueError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setValue('');
      setNotes('');
      setValueError(null);
    }
  }, [isOpen]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    if (!value || isNaN(parseFloat(value))) {
      setValueError(t('meters.validation_value_required', 'A valid numeric value is required'));
      return;
    }
    setValueError(null);

    const payload = {
      meterId,
      value: parseFloat(value),
      source: 'MANUAL',
      notes: notes.trim() || null,
    };

    dispatch(createMeterReading(payload, () => {
      if (onSave) onSave();
    }));
  }, [value, notes, meterId, dispatch, onSave, t]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <div className="modal-header text-white">
          <h5 className="modal-title fs-4">
            {t('meters.record_reading', 'Record Reading')}
            {meterName && <small className="ms-2" style={{ opacity: 0.8 }}>- {meterName}</small>}
          </h5>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={toggle}
            aria-label={t('shared.close', 'Close')}
          ></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body p-4">
            {saveError && <Alert color="danger" className="mb-3">{saveError}</Alert>}

            <div className="form-group mb-4">
              <label htmlFor="readingValue" className="form-label fw-medium">
                {t('shared.value', 'Value')} <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                step="any"
                id="readingValue"
                className={`form-control ${valueError ? 'is-invalid' : ''}`}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setValueError(null);
                }}
                placeholder={t('meters.value_placeholder', 'Enter reading value')}
                autoFocus
              />
              {valueError && <small className="text-danger">{valueError}</small>}
            </div>

            <div className="form-group">
              <label htmlFor="readingNotes" className="form-label fw-medium">
                {t('meters.notes', 'Notes')}
              </label>
              <textarea
                id="readingNotes"
                className="form-control"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('meters.notes_placeholder', 'Optional notes about this reading')}
              />
            </div>
          </div>

          <div className="modal-footer bg-light border-top-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={toggle}
              disabled={saving}
            >
              {t('shared.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('shared.saving', 'Saving...')}
                </>
              ) : (
                t('meters.submit_reading', 'Submit Reading')
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </Modal>
  );
};

export default MeterReadingForm;
